/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useState } from 'react';
import { Form, FormInstance } from 'antd';

interface Step5ContentProps {
  form: FormInstance;
}

const labelClass =
  'block text-[15px] sm:text-[16px] font-semibold text-[#111827] mb-2';

const inputBase =
  'w-full h-[52px] sm:h-[56px] rounded-xl border bg-white px-4 sm:px-5 pr-12 ' +
  'text-[16px] sm:text-[17px] text-[#111827] placeholder:text-[#9CA3AF] ' +
  'outline-none transition';

const focusGreen =
  'focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/20';
const borderDefault = 'border-[#D1D5DB]';

const Required = () => <span className="ml-1 text-[#DC2626]">*</span>;

function EyeIcon({ open }: { open: boolean }) {
  return open ? (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 3l18 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M10.58 10.58A3 3 0 0012 15a3 3 0 002.42-4.42"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M9.88 5.09A10.46 10.46 0 0112 5c7 0 10 7 10 7a18.2 18.2 0 01-4.33 5.06"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M6.61 6.61A18.24 18.24 0 002 12s3 7 10 7c1.03 0 2.02-.15 2.95-.43"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ) : (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M12 15a3 3 0 100-6 3 3 0 000 6z"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}

export default function Step5Content({ form }: Step5ContentProps) {
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="space-y-1">
        <h4 className="text-[16px] sm:text-[18px] font-semibold text-[#111827]">
          Set password
        </h4>
        <p className="text-[13px] sm:text-[14px] text-[#6B7280] leading-relaxed">
          Create a strong password to secure your account.
        </p>
      </div>

      <Form.Item
        name="password"
        className="mb-0"
        rules={[
          { required: true, message: 'Password is required' },
          { min: 8, message: 'Password must be at least 8 characters' },
          {
            pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
            message:
              'Password must contain at least one uppercase letter, one lowercase letter, and one number',
          },
        ]}
      >
        <div>
          <label className={labelClass}>
            Password
            <Required />
          </label>

          <div className="relative">
            <input
              type={showPass ? 'text' : 'password'}
              className={`${inputBase} ${borderDefault} ${focusGreen}`}
              placeholder="Enter your password"
              autoComplete="new-password"
            />

            <button
              type="button"
              aria-label={showPass ? 'Hide password' : 'Show password'}
              onClick={() => setShowPass((s) => !s)}
              className="
                absolute right-3 top-1/2 -translate-y-1/2
                inline-flex h-9 w-9 items-center justify-center
                rounded-lg text-[#6B7280]
                hover:text-[#111827]
                hover:bg-[#F3F4F6]
                focus:outline-none focus:ring-2 focus:ring-[#16A34A]/25
              "
            >
              <EyeIcon open={showPass} />
            </button>
          </div>
        </div>
      </Form.Item>

      <Form.Item
        name="confirm_password"
        className="mb-0"
        dependencies={['password']}
        rules={[
          { required: true, message: 'Confirm Password is required' },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value)
                return Promise.resolve();
              return Promise.reject(new Error('Passwords do not match'));
            },
          }),
        ]}
      >
        <div>
          <label className={labelClass}>
            Confirm Password
            <Required />
          </label>

          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              className={`${inputBase} ${borderDefault} ${focusGreen}`}
              placeholder="Confirm your password"
              autoComplete="new-password"
            />

            <button
              type="button"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
              onClick={() => setShowConfirm((s) => !s)}
              className="
                absolute right-3 top-1/2 -translate-y-1/2
                inline-flex h-9 w-9 items-center justify-center
                rounded-lg text-[#6B7280]
                hover:text-[#111827]
                hover:bg-[#F3F4F6]
                focus:outline-none focus:ring-2 focus:ring-[#16A34A]/25
              "
            >
              <EyeIcon open={showConfirm} />
            </button>
          </div>
        </div>
      </Form.Item>

      <div className="rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3">
        <p className="text-sm text-[#374151] font-semibold mb-2">
          Password requirements
        </p>
        <ul className="text-[12px] sm:text-[13px] text-[#6B7280] space-y-1">
          <li>• At least 8 characters long</li>
          <li>• Contains at least one uppercase letter (A-Z)</li>
          <li>• Contains at least one lowercase letter (a-z)</li>
          <li>• Contains at least one number (0-9)</li>
        </ul>
      </div>
    </div>
  );
}
